import {
  CustomerServiceTag,
  QuoteApiModel,
  QuoteOptionApiModel,
  QuoteProviderAccountApiModel
} from '~/lib/api/smartsend/types';
import { P2GGrid } from '@parcel2go/core-ui';
import { CurrentSelectionType } from '../utils/CurrentSelectionType';
import { QuoteTagType } from '../utils/QuoteTagType';
import { QuoteLayoutColumn } from './QuoteLayoutColumn';
import { ServiceSpeedTypeOptions } from '../utils/PopulateQuoteTags';
import { RoyalMailProvider } from '~/lib/consts/providers';
import { QuoteLayoutListView } from './QuoteLayoutListView';
import { useMemo } from 'react';
import { onlyUnique } from '~/lib/utils/array';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { QuoteLayoutVariant } from './QuoteLayoutVariant';
import { QuoteLayoutSingleCourier } from './QuoteLayoutSingleCourier';

interface props {
  onSave?: (quote: QuoteApiModel, option: QuoteOptionApiModel) => void;
  quotes: QuoteApiModel[];
  currentSelection: CurrentSelectionType;
  estimatedValue?: number;
  quoteTagType: QuoteTagType;
  updateServiceTags: (add: CustomerServiceTag[], remove: CustomerServiceTag[]) => void;
  serviceTags: CustomerServiceTag[];
  editMode: boolean;
  includeServices: (quotes: QuoteApiModel[]) => void;
  excludeServices: (quotes: QuoteApiModel[]) => void;
  selectedServiceProvider: QuoteProviderAccountApiModel;
  toggleSpecificTag: (tag: CustomerServiceTag) => void;
  isXs: boolean;
  children?: React.ReactNode;
}

export const QuoteLayoutDisplay: React.FC<props> = (props) => {
  const { tenant } = useTenant();

  const showHeaders = useMemo(() => {
    return (
      props.quotes
        ?.flatMap((x) => x.Service.Tags)
        .filter((x) => ServiceSpeedTypeOptions.includes(x as any))
        .filter(onlyUnique).length > 1
    );
  }, [props.quotes]);

  const variant: QuoteLayoutVariant = useMemo(() => {
    if (props.selectedServiceProvider.Provider === RoyalMailProvider) {
      return 'OBA';
    }

    if (tenant?.toUpperCase().startsWith('PARCEL2GO.')) {
      return 'MULTI-COURIER';
    }

    return 'SINGLE-COURIER';
  }, [props.selectedServiceProvider.Provider, tenant]);

  if (props.quotes.length == 0) {
    return null;
  }

  const maxQuoteOptions = Math.max(...props.quotes.map((x) => x.Options.length));

  if (variant === 'OBA') {
    return (
      <QuoteLayoutListView
        variant={variant}
        toggleSpecificTag={props.toggleSpecificTag}
        updateServiceTags={props.updateServiceTags}
        onSave={props.onSave}
        includeServices={props.includeServices}
        excludeServices={props.excludeServices}
        currentSelection={props.currentSelection}
        editMode={props.editMode}
        quoteTagType={props.quoteTagType}
        quotes={props.quotes}
        serviceTags={props.serviceTags}
        children={props.children}
        estimatedValue={props.estimatedValue}
        maxQuoteOptions={maxQuoteOptions}
        isXs={props.isXs}
      />
    );
  }

  if (variant === 'MULTI-COURIER') {
    return (
      <P2GGrid container spacing={2} justify="center" mt={showHeaders ? 0 : 1}>
        {ServiceSpeedTypeOptions.map((x, i) => (
          <QuoteLayoutColumn
            variant={variant}
            showHeader={showHeaders}
            isXs={props.isXs}
            toggleSpecificTag={props.toggleSpecificTag}
            key={x}
            updateServiceTags={props.updateServiceTags}
            onSave={props.onSave}
            serviceType={x}
            index={i}
            includeServices={props.includeServices}
            excludeServices={props.excludeServices}
            currentSelection={props.currentSelection}
            editMode={props.editMode}
            quoteTagType={props.quoteTagType}
            quotes={props.quotes.filter((q) => q.Service.Tags.includes(x))}
            serviceTags={props.serviceTags}
            children={props.children}
            estimatedValue={props.estimatedValue}
            maxQuoteOptions={maxQuoteOptions}
          />
        ))}
      </P2GGrid>
    );
  }

  return (
    <QuoteLayoutSingleCourier
      variant={variant}
      isXs={props.isXs}
      toggleSpecificTag={props.toggleSpecificTag}
      updateServiceTags={props.updateServiceTags}
      onSave={props.onSave}
      currentSelection={props.currentSelection}
      quoteTagType={props.quoteTagType}
      quotes={props.quotes}
      serviceTags={props.serviceTags}
      children={props.children}
      estimatedValue={props.estimatedValue}
      maxQuoteOptions={maxQuoteOptions}
    />
  );
};
